import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Container, Settings2, Plus, Trash2, Percent, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { CONTAINER_TERMS_SECTIONS } from '../data/termsData';
import { ShippingInfoCard, EMPTY_SHIPPING, cleanShipping } from '../components/ShippingInfoCard';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UOM_COLORS = {
  '20FT': 'bg-blue-100 text-blue-700',
  '40FT': 'bg-indigo-100 text-indigo-700',
  'Container': 'bg-amber-100 text-amber-700',
  'Per NOA': 'bg-emerald-100 text-emerald-700',
  'Per Set': 'bg-purple-100 text-purple-700',
  '%': 'bg-rose-100 text-rose-700',
};

export default function ContainerQuotation() {
  const navigate = useNavigate();
  const [charges, setCharges] = useState([]);
  const [rows, setRows] = useState({});
  const [gst, setGst] = useState({ enabled: false, rate: 9 });
  const [customer, setCustomer] = useState({ customer_name: '', customer_email: '', customer_phone: '', company_address: '' });
  const [shipping, setShipping] = useState(EMPTY_SHIPPING);
  const [notes, setNotes] = useState('');
  const [validityDays, setValidityDays] = useState(30);
  const [saving, setSaving] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [editList, setEditList] = useState([]);
  const [savingList, setSavingList] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showSaveTpl, setShowSaveTpl] = useState(false);
  const [tplName, setTplName] = useState('');
  const [savingTpl, setSavingTpl] = useState(false);

  const loadTemplates = () => {
    axios.get(`${BACKEND_URL}/api/container-charges/templates`)
      .then((res) => setTemplates(res.data.templates || []))
      .catch(() => {});
  };

  const applyTemplate = (tpl) => {
    setRows((prev) => {
      const next = {};
      Object.keys(prev).forEach((id) => { next[id] = { ...prev[id], selected: false }; });
      (tpl.rows || []).forEach((r) => {
        if (next[r.charge_id]) {
          next[r.charge_id] = { selected: true, qty: r.qty, price: r.price, percentMode: r.percentMode, value: r.value };
        }
      });
      return next;
    });
    if (tpl.notes) setNotes(tpl.notes);
    toast.success(`Template "${tpl.name}" applied`);
  };

  const saveTemplate = async () => {
    if (!tplName.trim()) {
      toast.error('Give the template a name');
      return;
    }
    const selRows = charges.filter((c) => rows[c.id]?.selected).map((c) => {
      const r = rows[c.id];
      return { charge_id: c.id, qty: Number(r.qty) || 1, price: Number(r.price) || 0, percentMode: !!r.percentMode, value: Number(r.value) || 0 };
    });
    if (selRows.length === 0) {
      toast.error('Select at least one charge first');
      return;
    }
    setSavingTpl(true);
    try {
      await axios.post(`${BACKEND_URL}/api/container-charges/templates`, { name: tplName.trim(), rows: selRows, notes });
      toast.success('Template saved');
      setShowSaveTpl(false);
      setTplName('');
      loadTemplates();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save template'));
    } finally {
      setSavingTpl(false);
    }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/container-charges/templates/${tpl.id}`);
      loadTemplates();
    } catch {
      toast.error('Failed to delete template');
    }
  };

  const loadCharges = () => {
    axios.get(`${BACKEND_URL}/api/container-charges/`).then((res) => {
      const items = res.data.items || [];
      setCharges(items);
      setRows((prev) => {
        const next = {};
        items.forEach((c) => {
          next[c.id] = prev[c.id] || {
            selected: false, qty: 1, price: c.default_price,
            percentMode: c.is_percent, value: c.default_price,
          };
        });
        return next;
      });
    }).catch(() => toast.error('Failed to load charge list'));
  };

  useEffect(() => {
    loadCharges();
    loadTemplates();
    axios.get(`${BACKEND_URL}/api/settings/gst`).then((res) => setGst(res.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setRow = (id, patch) => setRows((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const baseSubtotal = useMemo(() => charges.reduce((sum, c) => {
    const r = rows[c.id];
    if (!r?.selected || c.is_percent) return sum;
    return sum + (Number(r.qty) || 0) * (Number(r.price) || 0);
  }, 0), [charges, rows]);

  const percentAmount = (c) => {
    const r = rows[c.id];
    if (!r) return 0;
    return r.percentMode ? baseSubtotal * ((Number(r.value) || 0) / 100) : (Number(r.value) || 0);
  };

  const subtotal = useMemo(() => charges.reduce((sum, c) => {
    const r = rows[c.id];
    if (!r?.selected) return sum;
    return sum + (c.is_percent ? percentAmount(c) : (Number(r.qty) || 0) * (Number(r.price) || 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, 0), [charges, rows, baseSubtotal]);

  const selectedCount = charges.filter((c) => rows[c.id]?.selected).length;
  const gstAmount = gst.enabled ? subtotal * ((Number(gst.rate) || 0) / 100) : 0;

  const handleSubmit = async () => {
    if (!customer.customer_name.trim() || !customer.customer_email.trim() || !customer.customer_phone.trim()) {
      toast.error('Please fill in customer name, email and phone');
      return;
    }
    if (selectedCount === 0) {
      toast.error('Select at least one charge item');
      return;
    }
    const lineItems = charges.filter((c) => rows[c.id]?.selected).map((c) => {
      const r = rows[c.id];
      let desc = c.description;
      if (c.uom && c.uom !== '%') desc += ` (${c.uom})`;
      if (c.note) desc += ` — "${c.note}"`;
      if (c.is_percent) {
        const amt = Math.round(percentAmount(c) * 100) / 100;
        if (r.percentMode) desc += ` @ ${r.value}%`;
        return { description: desc, quantity: 1, hours: 0, unit_price: amt, amount: amt };
      }
      const qty = Number(r.qty) || 0;
      const price = Number(r.price) || 0;
      return { description: desc, quantity: qty, hours: 0, unit_price: price, amount: Math.round(qty * price * 100) / 100 };
    });
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/quotations-billing/`, {
        customer_name: customer.customer_name,
        customer_email: customer.customer_email,
        customer_phone: customer.customer_phone,
        company_name: customer.customer_name,
        company_email: customer.customer_email,
        company_contact: customer.customer_phone,
        company_address: customer.company_address,
        line_items: lineItems,
        notes: notes ? `Container Trucking Quotation\n${notes}` : 'Container Trucking Quotation',
        validity_days: Number(validityDays) || 30,
        quote_type: 'container',
        terms_included: true,
        shipping_info: cleanShipping(shipping),
      });
      toast.success(`Container quotation ${res.data.document_number} created`);
      navigate(`/admin/billing/quotation/${res.data.quotation_id}`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create quotation'));
    } finally {
      setSaving(false);
    }
  };

  const saveList = async () => {
    setSavingList(true);
    try {
      await axios.put(`${BACKEND_URL}/api/container-charges/`, { items: editList });
      toast.success('Price list saved');
      setShowManage(false);
      loadCharges();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save price list'));
    } finally {
      setSavingList(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-40 pb-32">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <Button variant="outline" onClick={() => navigate('/admin/billing')} data-testid="container-quote-back-btn">
            <ArrowLeft size={18} className="mr-2" /> Back
          </Button>
          <Button variant="outline" onClick={() => { setEditList(charges.map((c) => ({ ...c }))); setShowManage(true); }} data-testid="manage-price-list-btn">
            <Settings2 size={16} className="mr-2" /> Manage Price List
          </Button>
        </div>

        {templates.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="template-chips">
            <span className="text-xs font-semibold text-gray-500 uppercase">Templates:</span>
            {templates.map((tpl) => (
              <span key={tpl.id} className="inline-flex items-center gap-1 bg-white border border-[#5c1a1a]/30 rounded-full pl-3 pr-1 py-1 text-sm">
                <button type="button" onClick={() => applyTemplate(tpl)} className="font-medium text-[#5c1a1a] hover:underline" data-testid={`apply-template-${tpl.name.replace(/\s+/g, '-')}`}>
                  {tpl.name}
                </button>
                <button type="button" onClick={() => deleteTemplate(tpl)} className="text-gray-400 hover:text-red-600 p-0.5" data-testid={`delete-template-${tpl.name.replace(/\s+/g, '-')}`}>
                  <Trash2 size={12} />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-[#5c1a1a] flex items-center justify-center">
            <Container size={24} className="text-amber-300" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Container Trucking Quotation</h1>
            <p className="text-sm text-gray-500">Tick the charges that apply — totals calculate automatically</p>
          </div>
        </div>

        <Card className="mb-6">
          <CardHeader className="pb-3"><CardTitle className="text-base">Customer Details</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Customer / Company Name *</Label>
              <Input value={customer.customer_name} onChange={(e) => setCustomer({ ...customer, customer_name: e.target.value })} data-testid="cq-customer-name" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={customer.customer_email} onChange={(e) => setCustomer({ ...customer, customer_email: e.target.value })} data-testid="cq-customer-email" />
            </div>
            <div>
              <Label>Phone *</Label>
              <Input value={customer.customer_phone} onChange={(e) => setCustomer({ ...customer, customer_phone: e.target.value })} data-testid="cq-customer-phone" />
            </div>
            <div>
              <Label>Address</Label>
              <Input value={customer.company_address} onChange={(e) => setCustomer({ ...customer, company_address: e.target.value })} data-testid="cq-customer-address" />
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Charges <span className="text-sm font-normal text-gray-400">({selectedCount} selected)</span></CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {charges.map((c) => {
              const r = rows[c.id] || {};
              const rowTotal = c.is_percent ? percentAmount(c) : (Number(r.qty) || 0) * (Number(r.price) || 0);
              return (
                <div key={c.id} className={`flex flex-wrap items-center gap-3 p-3 rounded-lg border transition-colors ${r.selected ? 'border-[#5c1a1a]/40 bg-amber-50/50' : 'border-transparent hover:bg-gray-50'}`} data-testid={`charge-row-${c.id}`}>
                  <Checkbox checked={!!r.selected} onCheckedChange={(v) => setRow(c.id, { selected: !!v })} data-testid={`charge-check-${c.id}`} />
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-gray-900">{c.description}</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${UOM_COLORS[c.uom] || 'bg-gray-100 text-gray-600'}`}>{c.uom}</span>
                    </div>
                    {c.note && <p className="text-xs text-gray-400 italic">"{c.note}"</p>}
                  </div>
                  {r.selected && (
                    c.is_percent ? (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setRow(c.id, { percentMode: !r.percentMode, value: r.percentMode ? Math.round(percentAmount(c) * 100) / 100 : c.default_price })}
                          className="flex items-center gap-1 text-xs font-semibold border rounded-full px-2.5 py-1 text-[#5c1a1a] border-[#5c1a1a]"
                          data-testid={`percent-toggle-${c.id}`}
                        >
                          {r.percentMode ? <><Percent size={12} /> % of subtotal</> : <><DollarSign size={12} /> fixed $</>}
                        </button>
                        <Input type="number" step="0.01" value={r.value} onChange={(e) => setRow(c.id, { value: e.target.value })} className="w-24 h-8 text-sm" data-testid={`charge-value-${c.id}`} />
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-400">Qty</span>
                          <Input type="number" min="1" value={r.qty} onChange={(e) => setRow(c.id, { qty: e.target.value })} className="w-16 h-8 text-sm" data-testid={`charge-qty-${c.id}`} />
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-400">$</span>
                          <Input type="number" step="0.01" value={r.price} onChange={(e) => setRow(c.id, { price: e.target.value })} className="w-24 h-8 text-sm" data-testid={`charge-price-${c.id}`} />
                        </div>
                      </div>
                    )
                  )}
                  <div className={`w-24 text-right font-semibold text-sm ${r.selected ? 'text-[#5c1a1a]' : 'text-gray-300'}`} data-testid={`charge-total-${c.id}`}>
                    ${r.selected ? rowTotal.toFixed(2) : '0.00'}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Notes (Terms & Conditions can be added later)</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="cq-notes" />
            </div>
            <div>
              <Label>Validity (days)</Label>
              <Input type="number" min="1" value={validityDays} onChange={(e) => setValidityDays(e.target.value)} className="w-32" data-testid="cq-validity" />
            </div>
          </CardContent>
        </Card>

        <ShippingInfoCard value={shipping} onChange={setShipping} />

        <Card className="mb-6" data-testid="cq-terms-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Terms &amp; Conditions <span className="text-sm font-normal text-gray-400">(auto-attached to the quotation PDF)</span></CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="multiple" className="space-y-2" data-testid="cq-terms-accordion">
              {CONTAINER_TERMS_SECTIONS.map((s) => (
                <AccordionItem key={s.id} value={s.id} className="border rounded-lg px-4">
                  <AccordionTrigger className="text-left text-sm font-semibold text-gray-800 hover:text-[#5c1a1a] hover:no-underline py-3" data-testid={`cq-terms-${s.id}`}>
                    {s.title}
                  </AccordionTrigger>
                  <AccordionContent>
                    <ul className="space-y-2 text-sm text-gray-600 leading-relaxed pb-2">
                      {s.clauses.map((c, i) => (
                        <li key={i}>{c}</li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>
      </div>

      {/* Sticky totals bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-[0_-4px_12px_rgba(0,0,0,0.06)] z-20">
        <div className="container mx-auto px-4 max-w-5xl py-3 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-6 text-sm">
            <div><span className="text-gray-400">Subtotal</span> <span className="font-bold text-gray-900 ml-1" data-testid="cq-subtotal">${subtotal.toFixed(2)}</span></div>
            {gst.enabled && <div><span className="text-gray-400">GST {gst.rate}%</span> <span className="font-bold ml-1">${gstAmount.toFixed(2)}</span></div>}
            <div className="text-base"><span className="text-gray-400">Total</span> <span className="font-bold text-[#5c1a1a] ml-1" data-testid="cq-total">${(subtotal + gstAmount).toFixed(2)}</span></div>
          </div>
          <Button onClick={handleSubmit} disabled={saving} className="bg-[#5c1a1a] hover:bg-[#4a1515] text-white" data-testid="cq-create-btn">
            {saving ? 'Creating...' : 'Create Quotation'}
          </Button>
          <Button variant="outline" onClick={() => setShowSaveTpl(true)} disabled={selectedCount === 0} data-testid="save-template-btn">
            Save as Template
          </Button>
        </div>
      </div>

      {/* Save Template dialog */}
      <Dialog open={showSaveTpl} onOpenChange={setShowSaveTpl}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Save as Template</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-500 -mt-2">Saves the currently ticked charges with their qty &amp; prices for one-click reuse.</p>
          <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="e.g. Standard 20FT Round Trip" data-testid="template-name-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveTpl(false)}>Cancel</Button>
            <Button onClick={saveTemplate} disabled={savingTpl} className="bg-[#5c1a1a] hover:bg-[#4a1515] text-white" data-testid="save-template-confirm-btn">
              {savingTpl ? 'Saving...' : 'Save Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Price List dialog */}
      <Dialog open={showManage} onOpenChange={setShowManage}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Master Price List</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {editList.map((c, i) => (
              <div key={c.id || i} className="grid grid-cols-12 gap-2 items-center" data-testid={`edit-charge-${i}`}>
                <Input className="col-span-4 h-9 text-sm" value={c.description} placeholder="Description" onChange={(e) => setEditList(editList.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} />
                <Input className="col-span-3 h-9 text-sm" value={c.note} placeholder="Note (optional)" onChange={(e) => setEditList(editList.map((x, j) => j === i ? { ...x, note: e.target.value } : x))} />
                <Input className="col-span-2 h-9 text-sm" value={c.uom} placeholder="UOM" onChange={(e) => setEditList(editList.map((x, j) => j === i ? { ...x, uom: e.target.value } : x))} />
                <Input className="col-span-2 h-9 text-sm" type="number" step="0.01" value={c.default_price} onChange={(e) => setEditList(editList.map((x, j) => j === i ? { ...x, default_price: e.target.value } : x))} />
                <button type="button" onClick={() => setEditList(editList.filter((_, j) => j !== i))} className="col-span-1 text-red-500 hover:text-red-700 flex justify-center" data-testid={`delete-charge-${i}`}>
                  <Trash2 size={16} />
                </button>
                <label className="col-span-12 -mt-1 flex items-center gap-1.5 text-xs text-gray-500 pl-1">
                  <input type="checkbox" checked={!!c.is_percent} onChange={(e) => setEditList(editList.map((x, j) => j === i ? { ...x, is_percent: e.target.checked } : x))} />
                  Percentage item (auto-calculates from subtotal)
                </label>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setEditList([...editList, { description: '', note: '', uom: 'Container', default_price: 0, is_percent: false }])} data-testid="add-charge-btn">
              <Plus size={14} className="mr-1" /> Add Charge
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManage(false)}>Cancel</Button>
            <Button onClick={saveList} disabled={savingList} className="bg-[#5c1a1a] hover:bg-[#4a1515] text-white" data-testid="save-price-list-btn">
              {savingList ? 'Saving...' : 'Save Price List'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
